import { Box, Button, Card, CardBody, HStack, Text } from "@chakra-ui/react";
import { CalculationArithmeticList } from "features/calculation/CalculationArithmeticList";
import { PermissionTypeEnum } from "gen/model/permissionTypeEnum";
import { TransactionCalculationAddOrSubtractEnum } from "gen/model/transactionCalculationAddOrSubtractEnum";
import React from "react";
import { useTranslation } from "react-i18next";
import { Restricted } from "shared/components/access/Restricted";
import { BankIcon } from "shared/icons/BankIcon";
import { CoinHandsIcon } from "shared/icons/CoinsHandIcon";
import { PiggyBankIcon } from "shared/icons/PiggyBankIcon";

export interface CalculationListEmptyStateProps {
  onClickAddCalculation: () => void;
}

export const CalculationListEmptyState: React.FC<
  CalculationListEmptyStateProps
> = ({ onClickAddCalculation }) => {
  const { t } = useTranslation(["calculation"]);

  return (
    <Card mb="24px">
      <CardBody textAlign="center" py="64px">
        <Text textStyle="h2" mb="24px">
          {t("calculation:title")}
        </Text>
        <Text textStyle="h5" mb="40px">
          {t("calculation:description")}
        </Text>
        <HStack
          alignItems="stretch"
          spacing={0}
          gap="16px"
          mb="40px"
          justifyContent="center"
          flexWrap="wrap"
          sx={{
            "& > *": {
              flexGrow: 1,
            },
          }}
        >
          <CalculationExampleCard
            icon={BankIcon}
            i18nKey="calculation:totalContributions"
          />
          <CalculationExampleCard
            icon={PiggyBankIcon}
            i18nKey="calculation:unfundedCommitment"
          />
          <CalculationExampleCard
            icon={CoinHandsIcon}
            i18nKey="calculation:managementFeeBasis"
          />
        </HStack>
        <Restricted permission={PermissionTypeEnum.config}>
          <Button onClick={onClickAddCalculation}>
            {t("calculation:continueToSetUp")}
          </Button>
        </Restricted>
      </CardBody>
    </Card>
  );
};

const CalculationExampleCard: React.FC<{
  icon: React.ComponentType;
  i18nKey: string;
}> = ({ icon, i18nKey }) => {
  const { t } = useTranslation(["common", "calculation"]);
  const title = t(`${i18nKey}.title` as never);
  const examples = t(`${i18nKey}.examples` as never, {
    returnObjects: true,
  }) as unknown as Array<{
    op: TransactionCalculationAddOrSubtractEnum;
    text: string;
  }>;
  const IconComponent = React.createElement(icon, {
    color: "#B4E2CC",
    filter: "drop-shadow(-2px 2px 0px currentColor)",
  } as never);
  return (
    <Box borderRadius="8px" bg="gray.50" maxWidth="300px" p="20px">
      <Box display="inline-block">{IconComponent}</Box>
      <Text textStyle="smallBold">{title}</Text>
      <Text textStyle="smallBold" color="gray.400" mt="2px">
        {t("common:example")}
      </Text>
      <CalculationArithmeticList>
        {examples.map(({ op, text }, index) => (
          <CalculationArithmeticList.Item addOrSubtract={op} key={index}>
            {text}
          </CalculationArithmeticList.Item>
        ))}
      </CalculationArithmeticList>
    </Box>
  );
};
